import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SourceTypeIds } from "../SourceTypeIds";
import {
  parseConnectionConfiguration,
  updateConnectionConfiguration,
} from "../source-settings/SourceSettingsConfiguration";
import type { SourceSettingsModel } from "../source-settings/SourceSettingsApiAccess";
import { NotionSourceSettingsApi } from "./NotionSourceSettings.Api";
import type { NotionSourceSettingsViewModel } from "./NotionSourceSettings.ViewModel.types";
import type {
  NotionConfiguration,
  NotionPage,
  NotionTodoConfiguration,
} from "./NotionSourceSettings.types";

const MIN_PAGE_SEARCH_TERM_LENGTH = 2;

export const useNotionSourceSettingsViewModel = (
  model: SourceSettingsModel,
): NotionSourceSettingsViewModel => {
  const editingConnectionId = model.editingConnection.id;
  const onUpdateRef = useRef(model.onUpdate);
  const sourceSettingsApiRef = useRef(model.sourceSettingsApi);
  const editingConnectionRef = useRef(model.editingConnection);
  const initialisedConnectionIdRef = useRef<string | null>(null);
  const previousProfileIdentityRef = useRef(
    `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`,
  );

  const [isSearchingPages, setIsSearchingPages] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    onUpdateRef.current = model.onUpdate;
  }, [model.onUpdate]);

  useEffect(() => {
    sourceSettingsApiRef.current = model.sourceSettingsApi;
  }, [model.sourceSettingsApi]);

  useEffect(() => {
    editingConnectionRef.current = model.editingConnection;
  }, [model.editingConnection]);

  const isVisible =
    model.editingConnection.sourceTypeId.trim().toLowerCase() ===
      SourceTypeIds.Notion && model.editingConnection.isAuthorised;

  const configuration = useMemo(
    () =>
      parseConnectionConfiguration<NotionConfiguration>(
        model.editingConnection.configuration,
      ),
    [model.editingConnection.configuration],
  );

  const todoConfiguration = configuration.todoConfiguration ?? {};

  // Initialize missing todo config values on first render per connection.
  useEffect(() => {
    if (!isVisible) {
      initialisedConnectionIdRef.current = null;
      return;
    }

    if (initialisedConnectionIdRef.current === editingConnectionId) {
      return;
    }

    initialisedConnectionIdRef.current = editingConnectionId;

    updateConnectionConfiguration<NotionConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => {
        const nextTodoConfiguration: NotionTodoConfiguration = {
          ...(cfg.todoConfiguration ?? {}),
          pages: cfg.todoConfiguration?.pages ?? [],
        };

        return {
          ...cfg,
          todoConfiguration: nextTodoConfiguration,
        };
      },
      { isDefault: true },
    );
  }, [editingConnectionId, isVisible]);

  // Reset page selection when auth/profile identity changes.
  useEffect(() => {
    const currentProfileIdentity = `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`;
    const previousProfileIdentity = previousProfileIdentityRef.current;
    previousProfileIdentityRef.current = currentProfileIdentity;

    if (!isVisible || previousProfileIdentity === currentProfileIdentity) {
      return;
    }

    updateConnectionConfiguration<NotionConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        todoConfiguration: {
          ...(cfg.todoConfiguration ?? {}),
          pages: [],
        },
      }),
    );
  }, [
    isVisible,
    model.editingConnection.isAuthorised,
    model.editingConnection.profileId,
    model.editingConnection.profileName,
  ]);

  const searchPages = useCallback(
    async (term: string): Promise<NotionPage[]> => {
      const trimmedTerm = term.trim();
      if (trimmedTerm.length < MIN_PAGE_SEARCH_TERM_LENGTH) {
        return [];
      }

      if (!isVisible || !model.boardId || !editingConnectionId) {
        return [];
      }

      const notionApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<NotionSourceSettingsApi>(
          SourceTypeIds.Notion,
        );

      if (!notionApi) {
        return [];
      }

      setIsSearchingPages(true);
      setHasError(false);
      try {
        return await notionApi.getPages(
          model.boardId,
          editingConnectionId,
          trimmedTerm,
        );
      } catch {
        setHasError(true);
        return [];
      } finally {
        setIsSearchingPages(false);
      }
    },
    [editingConnectionId, isVisible, model.boardId],
  );

  return {
    isVisible,
    importTodos: todoConfiguration.importTodos ?? true,
    mentionsUser: todoConfiguration.mentionsUser ?? false,
    selectedPages: todoConfiguration.pages ?? [],
    isSearchingPages,
    hasError,
    searchPages,

    onToggleImportTodos: (isEnabled: boolean) => {
      updateConnectionConfiguration<NotionConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          todoConfiguration: {
            ...(cfg.todoConfiguration ?? {}),
            importTodos: isEnabled,
          },
        }),
      );
    },

    onSelectPages: (pages: NotionPage[]) => {
      updateConnectionConfiguration<NotionConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          todoConfiguration: {
            ...(cfg.todoConfiguration ?? {}),
            pages,
          },
        }),
      );
    },

    onToggleMentionsUser: (isEnabled: boolean) => {
      updateConnectionConfiguration<NotionConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          todoConfiguration: {
            ...(cfg.todoConfiguration ?? {}),
            mentionsUser: isEnabled,
          },
        }),
      );
    },
  };
};
